import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { portalApi } from "@/lib/portalApi";
import { fmtAmount, equivalentFor, FX_DISCLAIMER } from "@/lib/currency";
import { toast } from "sonner";
import { CheckCircle, XCircle, MessageSquare, Download, ChevronLeft,
         Clock, AlertTriangle, RefreshCw, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const fmt = (v, ccy) => fmtAmount(v, ccy);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "—";

const STATUS_CONFIG = {
  sent:            { label: "Awaiting Review",  cls: "bg-amber-100 text-amber-700",  icon: Clock },
  viewed:          { label: "Under Review",     cls: "bg-blue-100 text-blue-700",    icon: Clock },
  reminder_sent:   { label: "Reminder Sent",    cls: "bg-orange-100 text-orange-700",icon: AlertTriangle },
  accepted:        { label: "Accepted",         cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  declined:        { label: "Declined",         cls: "bg-slate-100 text-slate-500",  icon: XCircle },
  expired:         { label: "Expired",          cls: "bg-slate-100 text-slate-400",  icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.viewed;
  const Ic = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${cfg.cls}`} data-testid="estimate-status-badge">
      <Ic className="h-3.5 w-3.5" />
      {cfg.label}
    </span>
  );
}

function LineItemsTable({ items, currency }) {
  if (!items?.length) return null;
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden" data-testid="estimate-line-items">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs text-slate-500 font-medium">
            <th className="px-4 py-3">Service</th>
            <th className="px-4 py-3 text-right">Qty</th>
            <th className="px-4 py-3 text-right">Unit Price</th>
            <th className="px-4 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.id || i} className="border-t border-slate-100">
              <td className="px-4 py-3">
                <div className="font-medium text-slate-800">{item.name || item.description}</div>
                {item.description && item.name && item.description !== item.name && (
                  <div className="text-xs text-slate-400 mt-0.5">{item.description}</div>
                )}
              </td>
              <td className="px-4 py-3 text-right text-slate-600">{item.quantity ?? 1}</td>
              <td className="px-4 py-3 text-right text-slate-600">
                {item.unit_price != null ? fmt(item.unit_price, currency) : "—"}
              </td>
              <td className="px-4 py-3 text-right font-medium text-slate-800">
                {fmt(item.amount ?? item.total ?? (item.unit_price * (item.quantity ?? 1)), currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TotalBlock({ est, cc }) {
  const ccy = est.currency || "USD";
  const eq = (v) => equivalentFor(v, ccy, { rate: cc.usd_thb_rate, showThb: cc.show_thb });
  const showEq = !!eq(est.total);
  return (
    <div className="bg-slate-50 rounded-xl p-4 space-y-1.5" data-testid="estimate-totals">
      <div className="flex justify-between text-sm text-slate-600">
        <span>Subtotal</span>
        <span>{fmt(est.subtotal, ccy)}</span>
      </div>
      {est.discount > 0 && (
        <div className="flex justify-between text-sm text-emerald-600">
          <span>Discount</span>
          <span>−{fmt(est.discount, ccy)}</span>
        </div>
      )}
      <div className="flex justify-between text-base font-bold text-slate-900 border-t border-slate-200 pt-2 mt-1">
        <span>Total</span>
        <div className="text-right">
          <div>{fmt(est.total, ccy)}</div>
          {showEq && <div className="text-xs font-normal text-slate-400">≈ {eq(est.total)}</div>}
        </div>
      </div>
      {showEq && (
        <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-100">{FX_DISCLAIMER}</p>
      )}
    </div>
  );
}

export default function PortalEstimate() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const [est, setEst] = useState(null);
  const [cc, setCc] = useState({ usd_thb_rate: 33, show_thb: false });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);

  // Modal states
  const [showAccept, setShowAccept] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showRevision, setShowRevision] = useState(false);
  const [agree, setAgree] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [question, setQuestion] = useState("");
  const [revisionMessage, setRevisionMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!estimateId) return;
    setLoading(true);
    setErr(null);
    portalApi.get(`/portal/estimates/${estimateId}`)
      .then((r) => {
        setEst(r.data);
        setLoading(false);
        // Fetch timeline (non-blocking — silenced on failure)
        portalApi.get(`/portal/estimates/${estimateId}/timeline`)
          .then((tl) => setHistoryEvents(tl.data?.events || []))
          .catch(() => {});
      })
      .catch((e) => {
        setErr(e.response?.data?.detail || "Estimate not found");
        setLoading(false);
      });
    portalApi.get("/portal/currency-context").then((r) => r?.data && setCc(r.data)).catch(() => {});
  }, [estimateId]);

  const handleAccept = async () => {
    if (!agree) { toast.error("Please confirm you agree to the estimated charges."); return; }
    setBusy(true);
    try {
      await portalApi.post(`/portal/estimates/${estimateId}/accept`, { agree: true });
      setEst((e) => ({ ...e, status: "accepted", accepted_at: new Date().toISOString() }));
      setShowAccept(false);
      toast.success("Estimate accepted — your tax team has been notified!");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept estimate"); }
    finally { setBusy(false); }
  };

  const handleDecline = async () => {
    setBusy(true);
    try {
      await portalApi.post(`/portal/estimates/${estimateId}/decline`, { reason: declineReason || null });
      setEst((e) => ({ ...e, status: "declined", declined_at: new Date().toISOString() }));
      setShowDecline(false);
      setDeclineReason("");
      toast.message("Estimate declined. Your tax team has been notified.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not decline estimate"); }
    finally { setBusy(false); }
  };

  const handleQuestion = async () => {
    if (!question.trim()) { toast.error("Please enter your question."); return; }
    setBusy(true);
    try {
      await portalApi.post(`/portal/estimates/${estimateId}/question`, { question: question.trim() });
      setShowQuestion(false);
      setQuestion("");
      toast.success("Your question has been sent to your tax team.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send question"); }
    finally { setBusy(false); }
  };

  const handleDownloadPdf = async () => {
    try {
      const resp = await portalApi.get(`/portal/estimates/${estimateId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${est?.estimate_number || estimateId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed — please try again."); }
  };

  const handleRequestRevision = async () => {
    setBusy(true);
    try {
      await portalApi.post(`/portal/estimates/${estimateId}/request-revision`,
        { message: revisionMessage.trim() || null });
      // Mark locally so button disappears immediately without re-fetch
      setEst((e) => ({ ...e, can_request_revision: false, revision_already_requested: true }));
      setShowRevision(false);
      setRevisionMessage("");
      toast.success("Your request has been sent to your tax team. They will be in touch shortly.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "revision_request_already_exists") {
        // Suppress the button locally so it doesn't show again
        setEst((prev) => ({ ...prev, can_request_revision: false, revision_already_requested: true }));
        setShowRevision(false);
        toast.message("Your request for a revised estimate has already been sent to the tax team.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not send revision request. Please try again.");
      }
    } finally { setBusy(false); }
  };

  const isActionable = est?.status && ["sent", "viewed", "reminder_sent"].includes(est.status);
  const isAccepted = est?.status === "accepted";
  const isDeclined = est?.status === "declined";

  if (loading) return (
    <div className="flex items-center justify-center min-h-64 text-slate-400 text-sm" data-testid="estimate-loading">
      Loading estimate…
    </div>
  );

  if (err || !est) return (
    <div className="max-w-lg mx-auto mt-16 text-center space-y-4" data-testid="estimate-not-found">
      <XCircle className="h-12 w-12 text-slate-300 mx-auto" />
      <p className="text-slate-500">{err || "Estimate not found."}</p>
      <button onClick={() => navigate("/portal")} className="text-sm text-blue-600 underline">Back to Dashboard</button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-20" data-testid="portal-estimate-page">

      {/* Back nav */}
      <button onClick={() => navigate("/portal")}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors">
        <ChevronLeft className="h-4 w-4" />Back to Dashboard
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-3 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-slate-400 mb-1">Estimate</div>
            <h1 className="text-xl font-semibold text-slate-900" data-testid="estimate-number">
              {est.estimate_number || "Draft Estimate"}
            </h1>
            {est.title && <p className="text-slate-600 mt-1">{est.title}</p>}
          </div>
          <StatusBadge status={est.status} />
        </div>

        {/* Key dates */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400">
          {est.sent_at && <span>Sent {fmtDate(est.sent_at)}</span>}
          {est.expiration_date && <span>Expires {fmtDate(est.expiration_date)}</span>}
          {est.accepted_at && <span className="text-emerald-600">Accepted {fmtDate(est.accepted_at)}</span>}
          {est.declined_at && <span className="text-slate-500">Declined {fmtDate(est.declined_at)}</span>}
        </div>

        {/* Accepted banner */}
        {isAccepted && (
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-2" data-testid="estimate-accepted-banner">
            <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-800">You have accepted this estimate</div>
              <div className="text-xs text-emerald-600 mt-0.5">Your tax team is preparing the next steps. You'll hear from us shortly.</div>
              <button onClick={handleDownloadPdf} data-testid="estimate-download-pdf-accepted"
                className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 border border-emerald-200 rounded-lg px-3 py-1.5 transition-colors">
                <Download className="h-3.5 w-3.5" />Download PDF
              </button>
            </div>
          </div>
        )}

        {/* Declined banner */}
        {isDeclined && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-2" data-testid="estimate-declined-banner">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-600">You declined this estimate.</div>
                {est.revision_already_requested && (
                  <div className="mt-2 text-xs text-slate-500 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2"
                    data-testid="estimate-revision-already-sent">
                    Your request for a revised estimate has been sent to the tax team.
                  </div>
                )}
                {est.can_request_revision && (
                  <button
                    onClick={() => {
                      setRevisionMessage("");
                      setShowRevision(true);
                    }}
                    data-testid="estimate-request-revision-btn"
                    className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg px-3 py-1.5 transition-colors">
                    <RefreshCw className="h-3.5 w-3.5" />Request a Revised Estimate
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scope */}
      {(est.scope_summary || est.assumptions) && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-3 shadow-sm">
          <h2 className="font-semibold text-slate-800">Scope of Services</h2>
          {est.scope_summary && <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{est.scope_summary}</p>}
          {est.assumptions && (
            <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
              <div className="font-medium mb-1">Assumptions</div>
              <p className="leading-relaxed">{est.assumptions}</p>
            </div>
          )}
        </div>
      )}

      {/* Line items + totals */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4 shadow-sm">
        <h2 className="font-semibold text-slate-800">Pricing Breakdown</h2>
        <LineItemsTable items={est.line_items} currency={est.currency} />
        <TotalBlock est={est} cc={cc} />
        {est.payment_terms && (
          <p className="text-xs text-slate-400 pt-1">Payment terms: {est.payment_terms}</p>
        )}
      </div>

      {/* Client notes from firm */}
      {est.client_notes && (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 text-sm text-amber-800" data-testid="estimate-client-notes">
          <div className="font-semibold mb-1">Note from Your Tax Team</div>
          <p className="leading-relaxed whitespace-pre-wrap">{est.client_notes}</p>
        </div>
      )}

      {/* Actions */}
      {isActionable && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-3 shadow-sm" data-testid="estimate-actions">
          <h2 className="font-semibold text-slate-800">Your Response</h2>
          <p className="text-sm text-slate-500">Please review the estimate above and let us know how you'd like to proceed.</p>
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button onClick={() => setShowAccept(true)} data-testid="estimate-accept-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl px-5 py-3 transition-colors">
              <Check className="h-4 w-4" />Accept Estimate
            </button>
            <button onClick={() => setShowDecline(true)} data-testid="estimate-decline-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium rounded-xl px-5 py-3 transition-colors">
              <X className="h-4 w-4" />Decline
            </button>
          </div>
          <button onClick={() => setShowQuestion(true)} data-testid="estimate-ask-btn"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors w-full justify-center py-2">
            <MessageSquare className="h-4 w-4" />Ask a Question
          </button>
        </div>
      )}

      {/* Ask a question available on non-actionable estimates too (except declined/expired) */}
      {!isActionable && !isDeclined && est.status !== "expired" && (
        <div className="text-center">
          <button onClick={() => setShowQuestion(true)} data-testid="estimate-ask-btn"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors py-2">
            <MessageSquare className="h-4 w-4" />Have a question about this estimate?
          </button>
        </div>
      )}

      {/* PDF download — available on all resolved/active states */}
      {est.status && !["expired"].includes(est.status) && (
        <div className="text-center" data-testid="estimate-pdf-download-section">
          <button onClick={handleDownloadPdf} data-testid="estimate-download-pdf"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors py-1.5">
            <Download className="h-3.5 w-3.5" />Download as PDF
          </button>
        </div>
      )}

      {/* Estimate History — client-safe milestone timeline */}
      {historyEvents.length > 0 && (
        <div className="border border-slate-100 rounded-xl p-4" data-testid="estimate-history-section">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Estimate History</div>
          <ol className="relative border-l border-slate-200 ml-2 space-y-0">
            {historyEvents.map((ev, i) => {
              const isPortal = ev.actor_role === "portal";
              return (
                <li key={i} className="mb-0 ml-5 pb-4 last:pb-0">
                  <span className={`absolute -left-[7px] flex items-center justify-center w-3.5 h-3.5 rounded-full ring-2 ring-white
                    ${isPortal ? "bg-blue-500" : "bg-slate-300"}`} />
                  <div className="text-xs font-semibold text-slate-700">{ev.title}</div>
                  <time className="block text-[10px] text-slate-400 mt-0.5">
                    {ev.timestamp ? new Date(ev.timestamp).toLocaleString(undefined, {dateStyle:"medium", timeStyle:"short"}) : ""}
                  </time>
                  {ev.description && ev.description.length > 0 && (
                    <p className="mt-0.5 text-xs text-slate-500 leading-snug">{ev.description}</p>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Accept Confirmation Modal */}
      <Dialog open={showAccept} onOpenChange={(o) => { setShowAccept(o); if (!o) setAgree(false); }}>
        <DialogContent className="max-w-md" data-testid="estimate-accept-modal">
          <DialogHeader>
            <DialogTitle>Accept Estimate</DialogTitle>
            <DialogDescription>
              You are about to accept <strong>{est.estimate_number}</strong> totalling{" "}
              <strong>{fmt(est.total, est.currency)}</strong>. Your tax team will be notified immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                data-testid="estimate-agree-checkbox" className="mt-0.5 accent-emerald-600 h-4 w-4" />
              <span className="text-sm text-slate-700">
                I agree to the estimated services and charges outlined in this estimate.
              </span>
            </label>
            <div className="flex gap-2 pt-1">
              <Button onClick={handleAccept} disabled={!agree || busy} data-testid="estimate-confirm-accept"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy ? "Processing…" : "Confirm Acceptance"}
              </Button>
              <Button variant="outline" onClick={() => { setShowAccept(false); setAgree(false); }}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline Modal */}
      <Dialog open={showDecline} onOpenChange={(o) => { setShowDecline(o); if (!o) setDeclineReason(""); }}>
        <DialogContent className="max-w-md" data-testid="estimate-decline-modal">
          <DialogHeader>
            <DialogTitle>Decline Estimate</DialogTitle>
            <DialogDescription>Let us know if there's a reason — this helps your team improve the proposal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Optional — reason for declining (e.g. budget, scope, timing)"
              data-testid="estimate-decline-reason" rows={3} />
            <div className="flex gap-2">
              <Button onClick={handleDecline} disabled={busy} variant="outline"
                data-testid="estimate-confirm-decline" className="flex-1 border-slate-300">
                {busy ? "Declining…" : "Decline Estimate"}
              </Button>
              <Button variant="outline" onClick={() => { setShowDecline(false); setDeclineReason(""); }}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ask a Question Modal */}
      <Dialog open={showQuestion} onOpenChange={(o) => { setShowQuestion(o); if (!o) setQuestion(""); }}>
        <DialogContent className="max-w-md" data-testid="estimate-question-modal">
          <DialogHeader>
            <DialogTitle>Ask a Question</DialogTitle>
            <DialogDescription>Your question will be sent directly to your tax team about estimate {est.estimate_number}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Textarea value={question} onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Can you clarify the scope for tax filing services?"
              data-testid="estimate-question-input" rows={3} />
            <div className="flex gap-2">
              <Button onClick={handleQuestion} disabled={!question.trim() || busy} data-testid="estimate-question-send"
                className="flex-1 bg-slate-800 text-white hover:bg-slate-700">
                {busy ? "Sending…" : "Send Question"}
              </Button>
              <Button variant="outline" onClick={() => { setShowQuestion(false); setQuestion(""); }}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Revised Estimate Modal */}
      <Dialog open={showRevision} onOpenChange={(o) => { setShowRevision(o); if (!o) setRevisionMessage(""); }}>
        <DialogContent className="max-w-md" data-testid="estimate-revision-modal">
          <DialogHeader>
            <DialogTitle>Request a Revised Estimate</DialogTitle>
            <DialogDescription>
              Your message will be sent to your tax team regarding estimate{" "}
              <strong>{est.estimate_number}</strong>. They will prepare a new estimate for your review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Subject line — display only, not editable */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="estimate-revision-subject">
              <div className="text-xs text-slate-400 mb-0.5">Subject</div>
              <div className="text-sm text-slate-700 font-medium">
                Request for Revised Estimate — {est.estimate_number}
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Message (optional — edit as needed)</label>
              <Textarea
                value={revisionMessage}
                onChange={(e) => setRevisionMessage(e.target.value)}
                placeholder="I would like to request a revised estimate. Please let me know if you need any additional information from me before preparing it."
                data-testid="estimate-revision-message"
                rows={4}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleRequestRevision}
                disabled={busy}
                data-testid="estimate-revision-send"
                className="flex-1 bg-blue-700 text-white hover:bg-blue-800">
                {busy ? "Sending…" : "Send Request"}
              </Button>
              <Button variant="outline" onClick={() => { setShowRevision(false); setRevisionMessage(""); }}
                data-testid="estimate-revision-cancel">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
